import React, { useEffect } from "react";
import { useAppSelector } from "../store/hooks";
import { messengerApi, useSubscribePushMutation } from "../services/rtkApi/messengerApi";
import {
  PushDeviceReport,
  useReportPushDeviceMutation,
} from "../services/rtkApi/messengerReachApi";
import { isIOS, isStandalone } from "../utils/pwa";

const SESSION_KEY = "n450s:push-resynced";
const INSTALL_ID_KEY = "n450s:install-id";
// let auth bootstrap + first paint settle before doing background maintenance
const RESYNC_DELAY_MS = 8000;

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const out = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) out[i] = rawData.charCodeAt(i);
  return out;
}

// stable random id for this browser install, so the messenger can tell devices apart
function getInstallId(): string | null {
  try {
    let id = localStorage.getItem(INSTALL_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem(INSTALL_ID_KEY, id);
    }
    return id;
  } catch {
    return null; // storage blocked: skip the device report, keep the resync
  }
}

function subscriptionBody(sub: PushSubscription) {
  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return null;
  return { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } };
}

/**
 * Headless boot-time healer + reporter for web push, once per session.
 *
 * - Re-upserts this browser's current subscription. Push endpoints rot silently: the push
 *   service can rotate them (the SW re-subscribes on `pushsubscriptionchange` but has no
 *   auth to tell the server), and the messenger disables endpoints that 404/410 on send.
 * - Reports this install's state (installed app or browser tab, notification permission,
 *   whether it holds a subscription) so admins can see who actually has the app and push.
 * - Re-subscribes a browser that still allows notifications but lost its subscription
 *   (Safari revokes one after pushes that showed no banner). The messenger only asks for
 *   this when the lost subscription still exists server-side, so a user who switched push
 *   off is left alone.
 */
const PushResyncListener: React.FC = () => {
  const loggedIn = useAppSelector(
    (s) => s.auth.bootstrapStatus === "ready" && !!s.auth.token,
  );
  const [subscribePush] = useSubscribePushMutation();
  const [reportDevice] = useReportPushDeviceMutation();
  const [fetchVapid] = messengerApi.useLazyGetVapidPublicKeyQuery();

  useEffect(() => {
    if (!loggedIn) return;
    try {
      if (sessionStorage.getItem(SESSION_KEY) === "1") return;
    } catch {
      /* storage blocked — resync anyway */
    }

    const t = setTimeout(async () => {
      const pushSupported =
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        typeof Notification !== "undefined";
      const permission: PushDeviceReport["permission"] = pushSupported
        ? Notification.permission
        : "unsupported";
      const installId = getInstallId();

      try {
        let reg: ServiceWorkerRegistration | undefined;
        let sub: PushSubscription | null = null;
        if (pushSupported && permission === "granted") {
          reg = await navigator.serviceWorker.getRegistration();
          sub = (await reg?.pushManager.getSubscription()) ?? null;
          const body = sub ? subscriptionBody(sub) : null;
          if (body) await subscribePush(body).unwrap();
        }

        if (installId) {
          const base: PushDeviceReport = {
            installId,
            standalone: isStandalone(),
            permission,
            pushSupported,
            // iPadOS reports itself as a Mac; the UA alone would hide it
            ...(isIOS() ? { platform: "ios" as const } : {}),
          };
          const res = await reportDevice({ ...base, endpoint: sub?.endpoint ?? null }).unwrap();

          if (!sub && reg && res.data?.resubscribe) {
            const vapid = await fetchVapid().unwrap();
            const key = vapid.data?.publicKey;
            if (key && vapid.data?.configured) {
              const fresh = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(key),
              });
              const body = subscriptionBody(fresh);
              if (body) {
                await subscribePush(body).unwrap();
                await reportDevice({ ...base, endpoint: fresh.endpoint }).unwrap();
              }
            }
          }
        }

        try {
          sessionStorage.setItem(SESSION_KEY, "1");
        } catch {
          /* ignore */
        }
      } catch {
        // best-effort — next session retries (a re-subscribe Safari refuses without a tap
        // shows up in the admin reach view as "allowed, no subscription")
      }
    }, RESYNC_DELAY_MS);
    return () => clearTimeout(t);
  }, [loggedIn, subscribePush, reportDevice, fetchVapid]);

  return null;
};

export default PushResyncListener;
